import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import {
  getFirestore,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore'
import { getStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getAnalytics, isSupported } from 'firebase/analytics'
import config from './config/environment'
import { setupFirebaseErrorHandling } from './utils/firebaseErrorHandler'

// Firebase configuration
const firebaseConfig = {
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
  projectId: config.firebase.projectId,
  storageBucket: config.firebase.storageBucket,
  messagingSenderId: config.firebase.messagingSenderId,
  appId: config.firebase.appId,
  measurementId: config.firebase.measurementId
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// Offline-first Firestore: cached data loads even on bad / intermittent networks
export const auth = getAuth(app)
let db
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  })
} catch (e) {
  console.warn('Firestore persistent cache unavailable, falling back:', e?.message || e)
  db = getFirestore(app)
}
export { db }
export const storage = getStorage(app)
export const functions = getFunctions(app)

// Initialize Analytics safely (only in production and when supported)
export let analytics = null
if (config.isProduction && typeof window !== 'undefined') {
  isSupported().then(supported => {
    if (supported) {
      try {
        analytics = getAnalytics(app)
        console.log('📊 Firebase Analytics initialized')
      } catch (error) {
        console.warn('⚠️ Analytics initialization failed:', error.message)
      }
    } else {
      console.log('📊 Analytics not supported in this environment')
    }
  }).catch(error => {
    console.warn('⚠️ Analytics support check failed:', error.message)
  })
}

// Connect to emulators in development mode (if using local emulators)
if (config.isDevelopment && config.useEmulators) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
    connectFirestoreEmulator(db, 'localhost', 8080)
    connectStorageEmulator(storage, 'localhost', 9199)
    connectFunctionsEmulator(functions, 'localhost', 5001)
    console.log('🔧 Connected to Firebase emulators')
  } catch (error) {
    console.log('⚠️ Emulators not available, using production Firebase')
  }
}

// Setup global error handling for Firebase connection issues
if (typeof window !== 'undefined') {
  setupFirebaseErrorHandling()
}

export default app 